/**
 * Protocol constants — HUMAN DECISIONS.
 *
 * These numbers define what a measurement means and whether two surveys are
 * comparable. They are set by the program lead, not by a build session. If one
 * looks wrong, open a `needs-signoff` issue; never change it in code without a
 * decision recorded in docs/PROTOCOL.md.
 *
 * Source: docs/PROTOCOL.md (field rules) and docs/DESIGN_BRIEF.md.
 */

export const PROTOCOL = {
  /** Girth (stem circumference) is taken at this height on EVERY tree, from planting on. */
  GIRTH_BASAL_HEIGHT_CM: 10,

  /** Once a tree is taller than DBH_TRIGGER_HEIGHT_CM, girth is ALSO taken at this height (the DBH standard). */
  GIRTH_DBH_HEIGHT_CM: 130,

  /** Height above which the second (DBH) girth becomes required. */
  DBH_TRIGGER_HEIGHT_CM: 150,

  /** The only two heights a girth may be recorded at. */
  GIRTH_HEIGHTS_CM: [10, 130] as const,

  /** Height is measured with a tape or marked pole up to this; above it, a clinometer app and the method is recorded. */
  HEIGHT_TAPE_LIMIT_CM: 300,

  /** A tree recorded "not found" on this many consecutive surveys is closed as dead. */
  NOT_FOUND_SURVEYS_TO_DEAD: 2,

  /** Minutes a team searches with map and tag before recording NF. */
  NOT_FOUND_SEARCH_MINUTES: 2,

  /** A device fix farther than this from the tree's stored point flags a possible mis-tag for the lead. */
  MIS_TAG_DISTANCE_M: 15,

  /** Seconds the app averages the GPS fix when a tree is first registered. */
  REGISTRATION_FIX_AVERAGE_S: 15,

  /** Stressed on this many consecutive surveys raises a dashboard flag. */
  STRESSED_SURVEYS_TO_FLAG: 2,

  /** Photo resize target on the device before upload. */
  PHOTO_MAX_EDGE_PX: 1600,
  PHOTO_TARGET_BYTES: 300_000,

  /** Repeat-photo station: distance and compass direction from the tree. */
  PHOTO_STATION_DISTANCE_M: 2,
  PHOTO_STATION_BEARING_DEG: 180,

  /** A team-code session grants the field role for this long; then the team re-enters the code. */
  TEAM_SESSION_HOURS: 24,
} as const;

/** Tag IDs are site-zone-number, e.g. DS-A-017. Site 2–4 upper-case letters, zone 1–2, number 3 digits. */
export const TAG_PATTERN = /^[A-Z]{2,4}-[A-Z0-9]{1,2}-\d{3}$/;

/** Minimum survey rhythm, documented for the dashboard's "overdue" flag: end of rains and peak dry season. */
export const SURVEY_SEASONS = [
  { key: 'end_of_rains', months: [11, 12] },
  { key: 'peak_dry', months: [3, 4] },
] as const;
